import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

/**
 * Landing Page V2 - Flow-First Design
 * Objectif: Conversion immédiate vers l'analyse
 * Message: "Analyse un immeuble à revenus en 3 minutes. Décide en 30 secondes."
 */
const LandingPageV2 = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: 'Zap',
      title: '3 minutes',
      description: 'Pour analyser un immeuble complet'
    },
    {
      icon: 'Target',
      title: 'Verdict clair',
      description: 'Décision en 30 secondes'
    },
    {
      icon: 'TrendingUp',
      title: 'Projections',
      description: 'Visualisez sur 10-30 ans'
    }
  ];

  const testimonials = [
    {
      quote: "J'ai analysé 12 immeubles en une soirée. J'en ai acheté un.",
      author: "Martin L.",
      role: "Investisseur, Québec"
    },
    {
      quote: "Enfin un outil qui me dit clairement si ça vaut la peine.",
      author: "Sophie T.",
      role: "Première acheteuse, Montréal"
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: 'white'
    }}>
      {/* Header minimal */}
      <header style={{
        padding: '1.5rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <img src="/immoiq-logo.png" alt="ImmoIQ" style={{ height: '40px', width: 'auto' }} />
          <span style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            letterSpacing: '-0.02em'
          }}>
            ImmoIQ
          </span>
        </div>

        <button
          onClick={() => navigate('/login')}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            padding: '0.625rem 1.25rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255,255,255,0.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
          }}
        >
          Se connecter
        </button>
      </header>

      {/* Hero Section */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '4rem 2rem 6rem'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(59, 130, 246, 0.15)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            padding: '0.5rem 1rem',
            borderRadius: '100px',
            marginBottom: '2rem',
            fontSize: '0.875rem',
            color: '#60a5fa'
          }}>
            <Icon name="Sparkles" size={16} />
            <span>Nouveau: Analyse par IA en 1 clic</span>
          </div>

          {/* Titre principal */}
          <h1 style={{
            fontSize: 'clamp(2.5rem, 6vw, 4rem)',
            fontWeight: 800,
            lineHeight: 1.1,
            marginBottom: '1.5rem',
            letterSpacing: '-0.03em'
          }}>
            Analyse un immeuble
            <br />
            <span style={{
              background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              en 3 minutes
            </span>
          </h1>

          {/* Sous-titre */}
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '3rem',
            lineHeight: 1.6
          }}>
            Décide en 30 secondes si ça vaut la peine.
            <br />
            Aucune carte requise pour commencer.
          </p>

          {/* CTA Principal */}
          <button
            onClick={() => navigate('/v2/demo')}
            style={{
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              color: 'white',
              border: 'none',
              padding: '1.125rem 2.5rem',
              borderRadius: '12px',
              fontSize: '1.125rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              boxShadow: '0 4px 24px rgba(59, 130, 246, 0.4)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 24px rgba(59, 130, 246, 0.4)';
            }}
          >
            <span>Tester gratuitement</span>
            <Icon name="ArrowRight" size={20} />
          </button>

          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(255,255,255,0.5)',
            marginTop: '1rem'
          }}>
            Aucune inscription requise pour la première analyse
          </p>
        </div>

        {/* Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginTop: '6rem'
        }}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                padding: '2rem',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.25rem'
              }}>
                <Icon name={feature.icon} size={28} color="#60a5fa" />
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                marginBottom: '0.5rem'
              }}>
                {feature.title}
              </h3>
              <p style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '1rem'
              }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Social Proof */}
        <div style={{
          marginTop: '6rem',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(255,255,255,0.5)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '2rem'
          }}>
            Ce que disent nos utilisateurs
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '16px',
                  padding: '1.5rem',
                  textAlign: 'left'
                }}
              >
                <p style={{
                  fontSize: '1.125rem',
                  fontStyle: 'italic',
                  marginBottom: '1rem',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  "{testimonial.quote}"
                </p>
                <div>
                  <p style={{
                    fontWeight: 600,
                    marginBottom: '0.25rem'
                  }}>
                    {testimonial.author}
                  </p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255,255,255,0.5)'
                  }}>
                    {testimonial.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{
          marginTop: '6rem',
          textAlign: 'center',
          padding: '3rem',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))',
          borderRadius: '24px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 700,
            marginBottom: '1rem'
          }}>
            Prêt à analyser ton premier immeuble?
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '2rem'
          }}>
            Tu vois la photo. Débloque le film avec l'essai gratuit.
          </p>
          <button
            onClick={() => navigate('/v2/demo')}
            style={{
              background: 'white',
              color: '#0f172a',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <span>Commencer maintenant</span>
            <Icon name="ArrowRight" size={18} />
          </button>
        </div>
      </main>

      {/* Footer minimal */}
      <footer style={{
        borderTop: '1px solid rgba(255,255,255,0.1)',
        padding: '2rem',
        textAlign: 'center',
        color: 'rgba(255,255,255,0.4)',
        fontSize: '0.875rem'
      }}>
        <p>© {new Date().getFullYear()} ImmoIQ. Fait avec ❤️ au Québec.</p>
        <p style={{ marginTop: '0.5rem' }}>
          <a
            href="/main-dashboard"
            style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}
          >
            Retour à la version classique
          </a>
        </p>
      </footer>
    </div>
  );
};

export default LandingPageV2;
